<template>
  <form-block :title="tr('Ad creation method')">
    <info-text v-if="entity.output_campaign.isObjectiveEngagement" type="info">
      <template v-if="entity.isPageLikeCampaign">
        {{ tr('For engagement campaigns aimed at increasing Page Likes, only the ad creative creation method is available.') }}
      </template>
      <template v-else>
        {{ tr('For engagement campaigns, only post boosting is available as the ad creation method since the goal is to increase engagement on existing content') }}
      </template>
    </info-text>
    <info-text v-else-if="entity.isAppPromo" type="info">
      {{ tr('For app promotion campaigns, only the ad creative creation method is available.') }}
    </info-text>
    <info-text v-else-if="entity.isLeadFormAd" type="info">
      {{ tr('For ads that use a lead generation form, only the ad creative creation option is available.') }}
    </info-text>

    <ui-radio-group
      v-model="entity.settings.ad_creation_method"
      :disabled="disabledMethodSelector"
      class="flex flex-col gap-2.5"
    >
      <label
        v-for="(method, key) in methods"
        :key="'ad-creation-method--' + key"
        :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', disabledMethodSelector ? 'cursor-not-allowed' : 'cursor-pointer' ]"
      >
        <ui-radio-group-item :value="method.value" />
        {{ method.label }}
      </label>
    </ui-radio-group>

    <template v-if="entity.settings.ad_creation_method === 'creative'">
      <div class="flex flex-col gap-2">
        <ui-label>{{ tr('AI Improvements') }}</ui-label>
        <p class="m-0 text-sm text-muted-foreground">
          {{ tr('You can easily entrust Infinite∞Ai to automatically optimize certain elements of your ads, making your campaigns more effective.') }}
          {{ tr('AI creates and configures the various elements based on the characteristics of your target audience and the context of your advertisement to engage your audience as effectively as possible.') }}
        </p>
        <label
          v-for="(item, key) in aiImprovementFields"
          :key="'scaling-up-settins--ai--' + key"
          v-tooltip.right="tr(item.description)"
          :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', disabled || item.disabled ? 'cursor-not-allowed' : 'cursor-pointer' ]"
        >
          <ui-checkbox
            :model-value="aiImprovements.includes(item.machine_name)"
            :disabled="disabled || item.disabled"
            @update:model-value="(checked) => toggleAiImprovement(item.machine_name, checked)"
          />
          {{ item.label }}
        </label>
      </div>

      <div v-if="aiEnabled" class="flex flex-col gap-2">
        <ui-label>{{ tr('Landing page-aware ad text') }}</ui-label>
        <p class="m-0 text-sm text-muted-foreground">
          {{ tr('Infinite∞Ad analyzes the content of the CTA button\'s landing page (URL) and tailors the generated ad text variants to match it.') }}
        </p>
        <label :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', disabled ? 'cursor-not-allowed' : 'cursor-pointer' ]">
          <ui-checkbox
            :model-value="!!entity.settings.ai_landing_page_focus"
            :disabled="disabled"
            @update:model-value="(value) => entity.settings.ai_landing_page_focus = value"
          />
          {{ tr('Align ad text to the landing page') }}
        </label>
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Ad Language') }}</ui-label>
        <select-field
          v-model="entity.settings.language"
          :options="languages()"
          :reduce="(obj) => obj.value"
          label="name"
          :disabled="disabled"
          searchable
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <ui-label>{{ tr('Number of Ad Variations') }}</ui-label>
        <p class="m-0 text-sm text-muted-foreground">
          {{ tr('You can specify how many different variations should be created from the ad that will be scaled up.') }}
        </p>
        <number-input
          v-model="entity.settings.ad_variants"
          class="max-w-40"
          :step="1"
          :min="1"
          :max="5"
          :disabled="disabled"
        />
      </div>

      <div v-if="entity.settings.ad_variants > 1" class="flex flex-col gap-2">
        <label :class="[ 'flex w-fit items-center gap-2 text-sm text-foreground', disabled ? 'cursor-not-allowed' : 'cursor-pointer' ]">
          <ui-checkbox
            :model-value="!!entity.settings.ai_ad_with_original_content"
            :disabled="disabled"
            @update:model-value="(value) => entity.settings.ai_ad_with_original_content = value"
          />
          {{ tr('One of the created variations should be made without Infinite∞Ai') }}
        </label>

        <info-text v-if="!adVariantsValueIsValid">
          {{ tr('If you want to create multiple ad variations, you need to specify at least one ad element that the Infinite∞Ai can determine.') }}
        </info-text>
      </div>

      <ui-button variant="outline" class="w-fit" @click="() => openApCreativeSettings()">
        <settings-2-icon />
        {{ tr('Meta Advantage+ Creative Settings') }}
      </ui-button>
    </template>
  </form-block>
</template>

<script>
import { Settings2 } from '@lucide/vue';

import { AD_CREATION_METHOD, ORIGINAL_POST_LANGUAGE, RULE_ACTION, TARGETED_COUNTRY_LANGUAGE } from '@/consts.js';
import RuleFormBlockMixin from '@/mixins/RuleForm/Block.js';
import InfoText from '@/ui/components/InfoText.vue';
import MetaApCreativeFeaturesModal from '@/ui/components/MetaApCreativeFeaturesModal.vue';
import NumberInput from '@/ui/components/NumberInput.vue';
import SelectField from '@/ui/components/SelectField.vue';

export default {
  name: 'AdCreationMethodBlock',
  components: {
    InfoText,
    NumberInput,
    SelectField,
    Settings2Icon: Settings2,
  },
  mixins: [ RuleFormBlockMixin ],
  data: () => ({
    aiImprovements: [],
  }),
  computed: {
    methods() {
      switch (this.entity.action) {
        case RULE_ACTION.SCALING_UP:
          return [
            { label: this.tr('Copy original ad'), value: AD_CREATION_METHOD.COPY },
            { label: this.tr('Create new ad creative(s)'), value: AD_CREATION_METHOD.CREATIVE },
          ];

        case RULE_ACTION.START:
          return [
            { label: this.tr('Boosting original posts'), value: AD_CREATION_METHOD.POST_BOOST },
            { label: this.tr('Create new ad creative(s)'), value: AD_CREATION_METHOD.CREATIVE },
          ];
      }

      return [];
    },
    aiImprovementFields() {
      const list = [
        {
          label: this.tr('Primary text'),
          description: this.tr('Infinite∞Ai writes the main content to ensure your message reaches its target effectively and with impact.'),
          machine_name: 'ai_primary_text',
        },
        {
          label: this.tr('CTA Title and Description'),
          description: this.tr('Let Infinite∞Ai suggest a powerful title and description for your ad to capture even more attention.'),
          machine_name: 'ai_cta_content',
        },
        {
          label: this.tr('CTA Button type'),
          description: this.tr('Let AI choose the best button type (such as "Learn More" or "Buy Now") to support the effectiveness of your ad.'),
          machine_name: 'ai_cta_button',
        },
      ];

      if (this.entity.isPageLikeCampaign) {
        const disabledMessage = this.tr('This option is not available for engagement campaigns aimed at increasing Page Likes.');
        list[1].disabled = true;
        list[1].description = disabledMessage;
        list[2].disabled = true;
        list[2].description = disabledMessage;
      }

      return list;
    },
    ctaList() {
      // App destinations already come without NO_BUTTON from the getter.
      return this.$store.getters['app/facebookCallToActionsByOdax']({
        campaignObjective: this.entity.output_campaign?.objective,
        conversionLocation: this.entity.settings.adset_conversion_location,
        optimizationGoal: this.entity.settings.adset_optimization_goal,
      });
    },
    aiEnabled() {
      return this.entity.settings.ai_primary_text
        || this.entity.settings.ai_cta_content
        || this.entity.settings.ai_cta_button;
    },
    adVariantsValueIsValid() {
      return !(this.entity.settings.ad_variants > 1 && this.aiImprovements.length === 0);
    },
    disabledMethodSelector() {
      return this.disabled
        || this.entity.output_campaign.isObjectiveEngagement
        || this.entity.isPageLikeCampaign
        || this.entity.isAppPromo
        || this.entity.isLeadFormAd;
    },
  },
  watch: {
    'entity.output_campaign.isObjectiveEngagement': {
      immediate: true,
      handler(val) {
        if (val) {
          switch (this.entity.action) {
            case RULE_ACTION.SCALING_UP:
              this.entity.settings.ad_creation_method = AD_CREATION_METHOD.COPY;
              break;

            case RULE_ACTION.START:
              if (this.entity.isPageLikeCampaign) {
                this.entity.settings.ad_creation_method = AD_CREATION_METHOD.CREATIVE;
              }
              else {
                this.entity.settings.ad_creation_method = AD_CREATION_METHOD.POST_BOOST;
              }
              break;
          }
        }
      },
    },
    'entity.settings.ad_creation_method'(val) {
      if (val === 'creative') {
        if (!this.entity.settings.cta_button) {
          this.entity.settings.cta_button = this.ctaList[0].machine_name;
        }

        Object.keys(this.entity.settings.ap_creative_features).map(key => this.entity.settings.ap_creative_features[key] = true);
      }
      else if ('custom_cta_settings' in this.entity.settings) {
        this.entity.settings.custom_cta_settings = false;
        this.entity.settings.cta_button = null;
        this.entity.settings.cta_url = null;
        this.entity.settings.smart_url = true;

        Object.keys(this.entity.settings.ap_creative_features).map(key => this.entity.settings.ap_creative_features[key] = false);
      }
    },
    'entity.isAppPromo': {
      immediate: true,
      handler(val) {
        if (val) {
          this.entity.settings.ad_creation_method = AD_CREATION_METHOD.CREATIVE;
        }
      },
    },
    'entity.isLeadFormAd': {
      immediate: true,
      handler(val) {
        if (val) {
          this.entity.settings.ad_creation_method = AD_CREATION_METHOD.CREATIVE;
        }
      },
    },
    aiImprovements(newVal, oldVal) {
      const difference = newVal
        .filter(x => !oldVal.includes(x))
        .concat(oldVal.filter(x => !newVal.includes(x)));

      difference.forEach(field => this.entity.settings[field] = !this.entity.settings[field]);
    },
    aiEnabled(newVal) {
      if (!newVal) {
        this.entity.settings.ai_landing_page_focus = false;
      }
    },
  },
  created() {
    this.aiImprovementFields.forEach(item => {
      if (this.entity.settings[item.machine_name]) {
        this.aiImprovements.push(item.machine_name);
      }
    });
  },
  methods: {
    toggleAiImprovement(field, checked) {
      this.aiImprovements = checked
        ? [ ...this.aiImprovements, field ]
        : this.aiImprovements.filter(item => item !== field);
    },
    validate() {
      if (!this.adVariantsValueIsValid) {
        this.error(this.tr('If you want to create multiple ad variations, you need to specify at least one ad element that the Infinite∞Ai can determine.'));
        return false;
      }

      return true;
    },
    openApCreativeSettings() {
      this.$fullscreenModal.open(
        this,
        MetaApCreativeFeaturesModal,
        () => ({
          modelValue: this.entity.settings.ap_creative_features,
          disabled: this.disabled,
        }),
        {
          componentListeners: {
            'update:modelValue': value => this.entity.settings.ap_creative_features = value,
          },
        },
      );
    },
    languages() {
      const list = JSON.parse(JSON.stringify(this.$store.getters['app/languagesISO']));

      list.forEach(item => item.value = item.iso_639_1);

      list.sort((a, b) => a.name.localeCompare(b.name));

      list.unshift(
        {
          name: 'Original post language',
          value: ORIGINAL_POST_LANGUAGE,
        },
        {
          name: 'Targeted country language',
          value: TARGETED_COUNTRY_LANGUAGE,
        },
      );

      if (this.entity.settings.language === null) {
        this.entity.settings.language = list[0].value;
      }

      return list;
    },
  },
};
</script>
